import { ArrowRightLeft, ChevronDown } from "lucide-react";
import { useTranslation } from "@/lib/hooks/use-translation";

/**
 * The two floating stat cards over the MacBook on hero slide 2, rebuilt as
 * live HTML from the comp's exports (nodes with the same Thai titles) instead
 * of being flattened into the screenshot — so their bars can actually move.
 *
 * Both replay their entrance every time the slide becomes active, off the same
 * `[data-active="true"]` hook as the chart behind them; the bars themselves
 * grow in with `hero-card-bar` (scaleY, origin bottom) and `hero-card-fill`
 * (scaleX, origin left), delays staggered inline per bar.
 *
 * aria-hidden like slide 1's notification cards: they mimic app UI over a
 * screenshot of app UI, so screen readers should get the slide's one alt text
 * instead of a fake dashboard read out piecemeal.
 */

/** Comp chip shared by both cards' headers. */
function CompareChip() {
  const { t } = useTranslation();

  return (
    <span className="flex shrink-0 items-center gap-1 rounded-md bg-[#F7F7F7] px-1.5 py-1 text-[9px] font-semibold text-black/40">
      {t("home.mocks.tiers.compare")}
      <ChevronDown className="size-2.5 text-[#8A8A8A]" aria-hidden />
    </span>
  );
}

/** ผลกระทบส่วนลด/เงินคืน — four mini bars on a log-ish axis, per the comp. */
const IMPACT_BARS = [
  {
    labelKey: "revenue",
    value: "22K",
    valueClass: "text-[#BE60BE]",
    // Gradient fills + hard borders come straight from the comp's export.
    barClass:
      "h-[104px] rounded-t-[5px] border-x border-t border-[#EA8AEA] bg-gradient-to-b from-[#F3C8F3] to-white",
    delay: "0.55s",
  },
  {
    labelKey: "discount",
    value: "990",
    valueClass: "text-[#569E67]",
    barClass:
      "h-[15px] rounded-t-[5px] border-x border-t border-[#85DA99] bg-[#3FCE75]",
    delay: "0.65s",
  },
  {
    labelKey: "refund",
    value: "11,000",
    valueClass: "text-[#B64D10]",
    barClass:
      "h-[6px] rounded-t-[5px] border-x border-t border-[#FF8F2B] bg-gradient-to-b from-[#FCCE97] to-white",
    delay: "0.75s",
  },
  {
    labelKey: "netRevenue",
    value: "4,500",
    valueClass: "text-[#465CC2]",
    barClass:
      "h-[58px] rounded-t-[5px] border-x border-t border-[#798CE3] bg-gradient-to-b from-[#A5B5FF] to-white",
    delay: "0.85s",
  },
];

export function ImpactCard() {
  const { t } = useTranslation();

  return (
    <div
      aria-hidden
      className="w-full rounded-[10px] border border-[#E6E6E6] bg-white p-3 shadow-[-2px_2px_8px_rgba(0,0,0,0.05)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold leading-[17px] text-black">
            {t("home.mocks.heroCards.impactTitle")}
          </p>
          <p className="text-[8.5px] font-medium leading-[12px] text-black/40">
            {t("home.mocks.heroCards.impactSub")}
          </p>
        </div>
        <CompareChip />
      </div>

      <div className="mt-2 flex gap-1.5">
        {/* Log-ish y axis, labels only — the comp draws no ticks. */}
        <div className="flex h-[120px] flex-col justify-between text-right text-[7px] leading-none text-[#717182]">
          <span>50K</span>
          <span>10K</span>
          <span>5K</span>
          <span>1K</span>
          <span>0</span>
        </div>
        <div className="flex-1">
          <div className="relative flex h-[120px] items-end justify-around gap-2 border-b border-[#ADADAD]">
            {/* Dashed grid behind the bars. Horizontals are one per y tick —
                the bottom tick is the solid axis line itself — and verticals
                divide the four categories.

                25/50/75% is where the gaps fall: `justify-around` gives four
                38px bars half-spacing at the ends and full spacing between, so
                the boundaries land on the quarters to within a pixel or two at
                any card width. The bars come later in the DOM and are opaque,
                so each line is only drawn where there is no bar over it. */}
            <div className="pointer-events-none absolute inset-0" aria-hidden>
              {[0, 25, 50, 75].map((top) => (
                <div
                  key={`h${top}`}
                  className="absolute inset-x-0 border-t border-dashed border-[#E6E6E6]"
                  style={{ top: `${top}%` }}
                />
              ))}
              {[25, 50, 75].map((left) => (
                <div
                  key={`v${left}`}
                  className="absolute inset-y-0 border-l border-dashed border-[#E6E6E6]"
                  style={{ left: `${left}%` }}
                />
              ))}
            </div>
            {IMPACT_BARS.map((bar) => (
              <div key={bar.labelKey} className="relative flex w-[38px] flex-col justify-end">
                <span
                  className={`hero-card-pop mb-0.5 text-center text-[8px] font-bold ${bar.valueClass}`}
                  style={{ "--d": bar.delay } as React.CSSProperties}
                >
                  {bar.value}
                </span>
                <div
                  className={`hero-card-bar w-full ${bar.barClass}`}
                  style={{ "--d": bar.delay } as React.CSSProperties}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-around pt-1 text-[7px] font-medium text-black/60">
            {IMPACT_BARS.map((bar) => (
              <span key={bar.labelKey}>
                {t(`home.mocks.heroCards.${bar.labelKey}`)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ช่องทางรายได้ทั้งหมด — POS split into channels, each with a fill bar.
    Widths are the comp's own bar-to-track ratios. */
const CHANNEL_ROWS = [
  { labelKey: "dineIn", color: "#00709B", pct: 66, value: "฿892.3K", share: "12%", sub: false },
  { labelKey: "delivery", color: "#00A9E8", pct: 20, value: "฿456.8K", share: "35%", sub: false },
  { label: "RobinHood", color: "#78D3F5", pct: 51, value: "฿187.5K", share: "28%", sub: true },
  { label: "Grab", color: "#78D3F5", pct: 40, value: "฿298.4K", share: "42%", sub: true },
  { label: "Lineman", color: "#78D3F5", pct: 29, value: "฿152.6K", share: "18%", sub: true },
  { label: "Shopee Food", color: "#78D3F5", pct: 24, value: "฿78.5K", share: "8%", sub: true },
  { labelKey: "takeaway", color: "#BBE8FA", pct: 8, value: "฿163.4K", share: "15%", sub: false },
];

export function ChannelsCard() {
  const { t } = useTranslation();

  return (
    <div
      aria-hidden
      className="w-full rounded-lg border border-[#E6E6E6] bg-white p-2.5 shadow-[-2px_2px_9px_rgba(0,0,0,0.05)]"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold leading-[15px] text-black">
            {t("home.mocks.heroCards.channelsTitle")}
          </p>
          <p className="text-[8px] font-medium leading-[11px] text-black/40">
            {t("home.mocks.heroCards.channelsSub")}
          </p>
        </div>
        <CompareChip />
      </div>

      <div className="mt-1.5 flex items-center justify-between">
        <span className="text-[9px] font-medium text-black/60">
          {t("home.mocks.heroCards.posSystem")}
        </span>
        <span className="flex items-center gap-1">
          <span className="text-[8px] text-black/60">(฿2,093,023.00)</span>
          <span className="text-[9px] font-semibold text-black">55%</span>
        </span>
      </div>

      <div>
        {CHANNEL_ROWS.map((row, i) => (
          <div
            key={row.label ?? row.labelKey}
            className={`flex items-center gap-1.5 border-b border-[#E6E6E6] py-1 last:border-b-0 ${
              row.sub ? "pl-2.5" : ""
            }`}
          >
            <span className="flex w-[54px] shrink-0 items-center gap-1">
              <span
                className="size-1.5 shrink-0 rounded-[1px]"
                style={{ backgroundColor: row.color }}
              />
              <span
                className={`whitespace-nowrap text-black/60 ${
                  row.sub ? "text-[7px]" : "text-[8px]"
                }`}
              >
                {row.label ?? t(`home.mocks.heroCards.channels.${row.labelKey}`)}
              </span>
            </span>
            <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#F7F7F7]">
              <span
                className="hero-card-fill block h-full rounded-full"
                style={
                  {
                    width: `${row.pct}%`,
                    backgroundColor: row.color,
                    "--d": `${0.55 + i * 0.07}s`,
                  } as React.CSSProperties
                }
              />
            </span>
            <span
              className={`shrink-0 font-semibold text-black ${
                row.sub ? "text-[7px]" : "text-[8px]"
              }`}
            >
              {row.value}
            </span>
            <span
              className={`w-5 shrink-0 text-right ${
                row.sub ? "text-[7px] text-black/20" : "text-[7px] text-black/40"
              }`}
            >
              {row.share}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Unused import guard — the comp's chip on the sales table uses this icon;
    kept out until that card is built. */
export const _icons = { ArrowRightLeft };
